import { db } from '../../firebase/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  Timestamp 
} from 'firebase/firestore';

/**
 * Base Service Class for Admin Operations
 * Provides common CRUD operations and utility methods
 */
export class BaseService {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.collectionRef = collection(db, collectionName);
  }

  /**
   * Get all documents from a specific collection
   * @param {string} collectionName - Name of the collection to query
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of documents with IDs
   */
  async getAllFromCollection(collectionName, options = {}) {
    try {
      const collectionRef = collection(db, collectionName);
      let q = collectionRef;
      
      // Apply filters
      if (options.filters) {
        options.filters.forEach(filter => {
          q = query(q, where(filter.field, filter.operator, filter.value));
        });
      }
      
      // Apply ordering
      if (options.orderBy) {
        q = query(q, orderBy(options.orderBy.field, options.orderBy.direction || 'asc'));
      }
      
      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching ${collectionName}:`, error);
      throw new Error(`Failed to fetch ${collectionName}: ${error.message}`);
    }
  }

  /**
   * Get all documents from collection
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of documents with IDs
   */
  async getAll(options = {}) {
    try {
      let q = this.collectionRef;
      
      // Apply filters - ensure filters is an array
      if (options.filters && Array.isArray(options.filters)) {
        options.filters.forEach(filter => {
          q = query(q, where(filter.field, filter.operator, filter.value));
        });
      }
      
      // Apply ordering
      if (options.orderBy) {
        q = query(q, orderBy(options.orderBy.field, options.orderBy.direction || 'asc'));
      }
      
      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching ${this.collectionName}:`, error);
      throw new Error(`Failed to fetch ${this.collectionName}: ${error.message}`);
    }
  }

  /**
   * Get a single document by ID
   * @param {string} id - Document ID
   * @returns {Promise<Object>} Document data with ID
   */
  async getById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error(`${this.collectionName} not found`);
      }
    } catch (error) {
      console.error(`Error fetching ${this.collectionName} by ID:`, error);
      throw new Error(`Failed to fetch ${this.collectionName}: ${error.message}`);
    }
  }

  /**
   * Create a new document
   * @param {Object} data - Document data
   * @param {string} userId - User ID performing the action
   * @returns {Promise<Object>} Created document with ID
   */
  async create(data, userId = 'admin') {
    try {
      const docData = {
        ...data,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: userId,
        lastModifiedBy: userId
      };
      
      const docRef = await addDoc(this.collectionRef, docData);
      return {
        id: docRef.id,
        ...docData
      };
    } catch (error) {
      console.error(`Error creating ${this.collectionName}:`, error);
      throw new Error(`Failed to create ${this.collectionName}: ${error.message}`);
    }
  }

  /**
   * Update an existing document
   * @param {string} id - Document ID
   * @param {Object} data - Update data
   * @param {string} userId - User ID performing the action
   * @returns {Promise<Object>} Updated document
   */
  async update(id, data, userId = 'admin') {
    try {
      const docRef = doc(db, this.collectionName, id);
      const updateData = {
        ...data,
        updatedAt: Timestamp.now(),
        lastModifiedBy: userId
      };
      
      await updateDoc(docRef, updateData);
      
      // Return updated document
      return this.getById(id);
    } catch (error) {
      console.error(`Error updating ${this.collectionName}:`, error);
      throw new Error(`Failed to update ${this.collectionName}: ${error.message}`);
    }
  }

  /**
   * Delete a document
   * @param {string} id - Document ID
   * @returns {Promise<boolean>} Success status
   */
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(`Error deleting ${this.collectionName}:`, error);
      throw new Error(`Failed to delete ${this.collectionName}: ${error.message}`);
    }
  }

  /**
   * Batch operations for multiple documents
   * @param {Array} operations - Array of operations to perform
   * @returns {Promise<boolean>} Success status
   */
  async batchOperation(operations) {
    try {
      const batch = writeBatch(db);
      
      operations.forEach(op => {
        const docRef = doc(db, this.collectionName, op.id);
        
        switch (op.type) {
          case 'create':
            batch.set(docRef, {
              ...op.data,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
              createdBy: op.userId || 'admin',
              lastModifiedBy: op.userId || 'admin'
            });
            break;
          case 'update':
            batch.update(docRef, {
              ...op.data,
              updatedAt: Timestamp.now(),
              lastModifiedBy: op.userId || 'admin'
            });
            break;
          case 'delete':
            batch.delete(docRef);
            break;
          default:
            throw new Error(`Invalid operation type: ${op.type}`);
        }
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error(`Error in batch operation for ${this.collectionName}:`, error);
      throw new Error(`Failed to perform batch operation: ${error.message}`);
    }
  }

  /**
   * Check if document exists
   * @param {string} id - Document ID
   * @returns {Promise<boolean>} Exists status
   */
  async exists(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      console.error(`Error checking existence of ${this.collectionName}:`, error);
      return false;
    }
  }

  /**
   * Get count of documents
   * @param {Object} filters - Optional filters
   * @returns {Promise<number>} Document count
   */
  async getCount(filters = {}) {
    try {
      // Convert filters object to array format if needed
      let filterArray = [];
      if (filters && typeof filters === 'object') {
        // If filters is an object with filter properties, convert to array
        if (filters.field && filters.operator && filters.value !== undefined) {
          filterArray = [filters];
        }
        // If filters is already an array, use it directly
        else if (Array.isArray(filters)) {
          filterArray = filters;
        }
      }
      
      const documents = await this.getAll({ filters: filterArray });
      return documents.length;
    } catch (error) {
      console.error(`Error getting count of ${this.collectionName}:`, error);
      throw new Error(`Failed to get count: ${error.message}`);
    }
  }

  /**
   * Search documents by field value
   * @param {string} field - Field to search
   * @param {*} value - Value to search for
   * @returns {Promise<Array>} Matching documents
   */
  async searchByField(field, value) {
    try {
      const q = query(this.collectionRef, where(field, '==', value));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error searching ${this.collectionName}:`, error);
      throw new Error(`Failed to search: ${error.message}`);
    }
  }
} 